// global.js — header interactions + shared utilities
const on = (sel, evt, fn) => document.querySelectorAll(sel).forEach((el) => el.addEventListener(evt, fn));
const drawer = document.querySelector('[data-menu-drawer]');
const REDUCE_MOTION = !document.documentElement.classList.contains('force-motion')
  && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
on('[data-menu-open]', 'click', (e) => {
  if (drawer) {
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    const g = window.gsap;
    if (g && !REDUCE_MOTION) {
      const links = drawer.querySelectorAll('ul li');
      g.fromTo(drawer, { opacity: 0, y: -12 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power3.out' });
      g.fromTo(links, { y: 48, opacity: 0 }, { y: 0, opacity: 1, duration: 0.55, stagger: 0.07, delay: 0.12, ease: 'power3.out' });
    }
  }
  e.currentTarget.setAttribute('aria-expanded', 'true');
});
const closeMenu = () => {
  if (drawer) { drawer.hidden = true; document.body.style.overflow = ''; }
  document.querySelectorAll('[data-menu-open]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
};
on('[data-menu-close]', 'click', closeMenu);
drawer?.addEventListener('click', (e) => { if (e.target === drawer) closeMenu(); });
on('[data-cart-open]', 'click', () => document.dispatchEvent(new CustomEvent('cart:open')));
on('[data-search-open]', 'click', () => document.dispatchEvent(new CustomEvent('search:open')));
document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });

// Collection filters + sort: auto-submit by navigating with updated query.
//
// There are TWO [data-sort] selects on a collection page — one in the mobile
// filter sheet and one in the desktop toolbar. querySelector bound only the
// first, which is the mobile one, so on desktop the visible select had no
// listener at all and sorting silently did nothing. Bind every instance, and
// keep them in step so opening the sheet after sorting does not show a stale
// value.
const filterForm = document.querySelector('[data-filter-form]');
const sortSelects = [...document.querySelectorAll('[data-sort]')];

filterForm?.addEventListener('change', (e) => {
  // a sort change inside the sheet is handled by its own listener below
  if (e.target.matches('[data-sort]')) return;
  const params = new URLSearchParams(new FormData(filterForm));
  const sort = sortSelects.find((s) => s.value);
  if (sort) params.set('sort_by', sort.value);
  window.location.search = params.toString();
});

sortSelects.forEach((select) => {
  select.addEventListener('change', (e) => {
    sortSelects.forEach((s) => { s.value = e.target.value; });
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', e.target.value);
    // a sort applies to the whole collection, so start again from page 1
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });
});

// Horizontal sliders: [data-slider] wraps [data-slider-track] with optional
// [data-slider-prev] / [data-slider-next] arrows. Scroll-snap based; arrows
// scroll by one item. NOTE: do NOT put data-lenis-prevent on full-width tracks —
// it makes Lenis swallow vertical wheel over the slider and the page gets stuck.
// Horizontal scroll still works via arrows + native overflow-x.
function initSliders(root) {
  (root || document).querySelectorAll('[data-slider]:not([data-slider-ready])').forEach((slider) => {
    slider.setAttribute('data-slider-ready', '');
    const track = slider.querySelector('[data-slider-track]');
    if (!track) return;
    const prev = slider.querySelector('[data-slider-prev]');
    const next = slider.querySelector('[data-slider-next]');
    const step = () => {
      const first = track.firstElementChild;
      const w = first ? first.getBoundingClientRect().width : track.clientWidth * 0.8;
      return w + 12;
    };
    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    const upd = () => {
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      if (prev) prev.classList.toggle('opacity-30', atStart);
      if (next) next.classList.toggle('opacity-30', atEnd);
    };
    track.addEventListener('scroll', upd, { passive: true });
    window.addEventListener('resize', upd, { passive: true });

    // Optional autoplay: advance one item every N ms; pause on hover / touch /
    // focus and when the slider is scrolled out of view. Set data-slider-autoplay
    // (in ms) on the [data-slider] wrapper. Loops back to the start at the end.
    const autoplayMs = parseInt(slider.getAttribute('data-slider-autoplay'), 10);
    if (autoplayMs > 0) {
      let timer = null;
      const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
      const advance = () => {
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        if (atEnd) track.scrollTo({ left: 0, behavior: 'smooth' });
        else track.scrollBy({ left: step(), behavior: 'smooth' });
      };
      const start = () => { stop(); timer = setInterval(advance, autoplayMs); };
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('touchstart', stop, { passive: true });
      slider.addEventListener('focusin', stop);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => entries.forEach((en) => (en.isIntersecting ? start() : stop())), { threshold: 0.2 }).observe(slider);
      } else { start(); }
    }

    upd();
  });
}
initSliders();
document.addEventListener('shopify:section:load', (e) => initSliders(e.target));

// Header scroll behaviour: stays visible (sticky) at all times; adds .is-scrolled
// after 40px which collapses the announcement bar + fades in a translucent
// backdrop so the floating header reads clearly over content. Driven off both
// native scroll and Lenis (whichever fires).
(function () {
  const header = document.querySelector('[data-header]');
  if (!header) return;
  let ticking = false;
  const update = () => {
    const y = window.scrollY || window.pageYOffset || (window.__lenis && window.__lenis.actualScroll) || 0;
    header.classList.toggle('is-scrolled', y > 40);
    ticking = false;
  };
  const onScroll = () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } };
  window.addEventListener('scroll', onScroll, { passive: true });
  if (window.__lenis && window.__lenis.on) window.__lenis.on('scroll', onScroll);
  else document.addEventListener('lenis:ready', () => { if (window.__lenis) window.__lenis.on('scroll', onScroll); });
  update();
})();

// Product-card colour options. Hovering a swatch previews that colour, clicking
// it sticks and appends ?colour=<name> to every link on the card so the product
// page opens on the same colour. Delegated on document, so the cost is one
// listener no matter how many cards a collection renders.
(function () {
  const media = (card) => card.querySelector('[data-card-media]');

  function applyImage(card, url) {
    const box = media(card);
    const img = box && box.querySelector('img');
    if (!img) return;
    if (img.dataset.origSrc === undefined) {
      img.dataset.origSrc = img.getAttribute('src') || '';
      img.dataset.origSrcset = img.getAttribute('srcset') || '';
      img.dataset.origSizes = img.getAttribute('sizes') || '';
    }
    const revert = !url;
    const next = revert ? img.dataset.origSrc : url;
    if (!next) return;
    // srcset outranks src, so it has to go while a colour is showing
    const settled = img.getAttribute('src') === next
      && (revert ? (img.getAttribute('srcset') || '') === img.dataset.origSrcset : !img.hasAttribute('srcset'));
    if (settled) return;
    box.classList.add('is-swapping');
    const pre = new Image();
    pre.onload = pre.onerror = () => {
      if (revert) {
        if (img.dataset.origSrcset) img.setAttribute('srcset', img.dataset.origSrcset);
        if (img.dataset.origSizes) img.setAttribute('sizes', img.dataset.origSizes);
      } else {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
      }
      img.setAttribute('src', next);
      box.classList.remove('is-swapping');
    };
    pre.src = next;
  }

  function applyLink(card, colour) {
    card.querySelectorAll('[data-card-link]').forEach((a) => {
      // data-base-href is the clean product URL. Reading the href instead would
      // pick up the ?colour= a colour tile already carries and stack a second one.
      if (a.dataset.baseHref === undefined) a.dataset.baseHref = a.getAttribute('href') || '';
      const base = a.dataset.baseHref;
      a.setAttribute('href', colour ? base + (base.indexOf('?') > -1 ? '&' : '?') + 'colour=' + encodeURIComponent(colour) : base);
    });
  }

  // Colour tiles print the colour under the title; keep it honest when another
  // swatch is picked, otherwise the card reads "Pink" while showing the blue one.
  function applyLabel(card, colour, hex) {
    const text = card.querySelector('[data-card-colour-text]');
    const dot = card.querySelector('[data-card-colour-dot]');
    if (!text) return;
    if (text.dataset.orig === undefined) {
      text.dataset.orig = text.textContent;
      if (dot) dot.dataset.orig = dot.style.background;
    }
    text.textContent = colour || text.dataset.orig;
    if (dot) dot.style.background = (colour && hex) ? hex : dot.dataset.orig;
  }

  // Deselecting on a colour tile falls back to the colour that tile represents,
  // not to "no colour" — otherwise its link would quietly drop the ?colour=.
  function resetToDefault(card) {
    const def = card.dataset.cardDefaultColour || '';
    applyImage(card, null);
    applyLabel(card, null);
    applyLink(card, def || null);
    card.querySelectorAll('[data-card-swatch]').forEach((b) => b.classList.toggle('is-active', !!def && b.dataset.colour === def));
  }

  document.addEventListener('mouseover', (e) => {
    const btn = e.target.closest && e.target.closest('[data-card-swatch]');
    if (!btn) return;
    const card = btn.closest('[data-card]');
    if (card) applyImage(card, btn.dataset.src);
  });

  document.addEventListener('mouseout', (e) => {
    const card = e.target.closest && e.target.closest('[data-card]');
    if (!card || (e.relatedTarget && card.contains(e.relatedTarget))) return;
    const active = card.querySelector('[data-card-swatch].is-active');
    applyImage(card, active ? active.dataset.src : null);
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-card-swatch]');
    if (!btn) return;
    e.preventDefault();
    const card = btn.closest('[data-card]');
    if (!card) return;
    const wasActive = btn.classList.contains('is-active');
    card.querySelectorAll('[data-card-swatch]').forEach((b) => b.classList.remove('is-active'));
    if (wasActive) { resetToDefault(card); return; }
    btn.classList.add('is-active');
    applyImage(card, btn.dataset.src);
    applyLink(card, btn.dataset.colour);
    applyLabel(card, btn.dataset.colour, btn.dataset.hex);
  });
})();

// Collection / search infinite scroll. The numbered pagination is still rendered
// server-side — this hides it and takes over, so crawlers and no-JS visitors keep
// working links, and a failed fetch puts them straight back rather than stranding
// the shopper halfway down a list.
(function () {
  const grid = document.querySelector('[data-infinite-grid]');
  const pager = document.querySelector('[data-infinite-pagination]');
  if (!grid || !pager) return;
  let next = pager.getAttribute('data-next-url') || '';
  if (!next) return;

  const nav = pager.querySelector('nav');
  if (nav) nav.setAttribute('hidden', '');

  const ui = document.createElement('div');
  ui.className = 'infinite-ui';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'infinite-more';
  btn.textContent = 'Load more';
  const status = document.createElement('p');
  status.className = 'infinite-status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  ui.appendChild(btn);
  ui.appendChild(status);
  pager.appendChild(ui);

  let io = null;
  let busy = false;

  function stop(message) {
    if (io) { io.disconnect(); io = null; }
    btn.remove();
    status.textContent = message || '';
  }

  function load() {
    if (busy || !next) return;
    busy = true;
    btn.disabled = true;
    status.textContent = 'Loading…';
    fetch(next, { credentials: 'same-origin' })
      .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('[data-infinite-grid]');
        const nextPager = doc.querySelector('[data-infinite-pagination]');
        const added = incoming ? Array.from(incoming.children) : [];
        added.forEach((el) => grid.appendChild(el));
        // The reveal engine pre-hid the children that existed at page load and
        // never sees these, so make sure nothing can leave them at opacity 0.
        if (window.gsap && added.length) window.gsap.set(added, { opacity: 1, y: 0 });
        next = nextPager ? (nextPager.getAttribute('data-next-url') || '') : '';
        busy = false;
        btn.disabled = false;
        status.textContent = '';
        if (!next) { stop('You have reached the end.'); return; }
        // Re-observing re-fires while the button is still in range, so a short
        // page keeps filling; once a batch pushes it past the margin, it rests.
        if (io) { io.unobserve(btn); io.observe(btn); }
        if (window.ScrollTrigger && window.ScrollTrigger.refresh) window.ScrollTrigger.refresh();
      })
      .catch(() => {
        busy = false;
        btn.disabled = false;
        status.textContent = 'Could not load more products.';
        if (nav) nav.removeAttribute('hidden');  // hand the numbered pages back
        if (io) { io.disconnect(); io = null; }
      });
  }

  btn.addEventListener('click', load);
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) load(); });
    }, { rootMargin: '700px 0px' });
    io.observe(btn);
  }
})();

/* ---------------------------------------------------------------------------
   Cart page: gift capture, order note, discount code.        (CRO #80/#89/#91)

   Gift details save as cart ATTRIBUTES over AJAX on change, not on form submit.
   Checkout is reachable three ways here — the native /checkout link, the
   Shiprocket Fastrr button and the drawer — and only one of them submits this
   form, so anything that waited for submit would be lost most of the time.

   The note is a cart note rather than a line-item property so it applies to the
   order and does not fragment identical items into separate cart lines.
--------------------------------------------------------------------------- */
(function () {
  const root = document.querySelector('[data-gift-fields]');
  if (!root) return;

  const panel = root.querySelector('[data-gift-panel]');
  const toggle = root.querySelector('[data-gift-toggle]');
  const status = root.querySelector('[data-gift-status]');
  let timer = null;
  let lastSent = '';

  function collect() {
    const attributes = {};
    let note = null;
    root.querySelectorAll('input, select, textarea').forEach((el) => {
      if (!el.name) return;
      if (el.name === 'note') { note = el.value; return; }
      const m = el.name.match(/^attributes\[(.+)\]$/);
      if (!m) return;
      // an unchecked box must clear the attribute, not simply be skipped
      if (el.type === 'checkbox') attributes[m[1]] = el.checked ? el.value : '';
      else attributes[m[1]] = el.value;
    });
    return { attributes, note };
  }

  function save() {
    const payload = collect();
    const key = JSON.stringify(payload);
    if (key === lastSent) return;
    lastSent = key;
    if (status) status.textContent = 'Saving…';
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((r) => { if (!r.ok) throw new Error('save failed'); return r.json(); })
      .then(() => { if (status) status.textContent = 'Gift details saved.'; })
      .catch(() => {
        lastSent = '';                       // let the next edit retry
        if (status) status.textContent = 'Could not save just yet — we will ask again at checkout.';
      });
  }

  function queue() { clearTimeout(timer); timer = setTimeout(save, 700); }

  root.addEventListener('input', queue);
  root.addEventListener('change', queue);
  // a half-typed message should still survive leaving the page
  window.addEventListener('pagehide', () => { clearTimeout(timer); save(); });

  if (toggle && panel) {
    const sync = () => { panel.hidden = !toggle.checked; };
    toggle.addEventListener('change', sync);
    sync();
  }
})();

/* Cart page discount field. Mirrors the drawer's behaviour (cart-drawer.js) —
   set Shopify's discount cookie, read the cart back, then reload so the totals
   and any savings line are server-rendered rather than patched in by hand. */
(function () {
  const btn = document.querySelector('[data-cartpg-discount-apply]');
  if (!btn) return;
  const input = document.getElementById('cartpg-discount');
  const msg = document.querySelector('[data-cartpg-discount-msg]');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  btn.addEventListener('click', async () => {
    const code = (input && input.value || '').trim();
    if (!code) { if (msg) msg.textContent = 'Enter a code first.'; return; }
    btn.setAttribute('aria-busy', 'true');
    if (msg) msg.textContent = 'Applying…';
    try {
      await fetch(root + 'discount/' + encodeURIComponent(code)).catch(() => {});
      const cart = await (await fetch(root + 'cart.js')).json();
      if (msg) {
        msg.textContent = cart.total_discount > 0
          ? 'Code applied — you saved ₹' + Math.round(cart.total_discount / 100) + '.'
          : 'Code applied. Any discount shows at checkout.';
      }
      window.location.reload();
    } catch (e) {
      if (msg) msg.textContent = 'Could not apply that code. Please try again.';
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  if (input) input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); btn.click(); } });
})();
